import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LuArrowRight, LuCheck, LuX } from 'react-icons/lu';
import HeaderOne from '../../header/HeaderOne';
import FooterOne from '../../footer/FooterOne';
import Head from '../../components/Head';
import '../landing.css';

// Each cell is either a string (with optional good/bad styling) or an object
// { value, type } where type ∈ "good" | "bad" | "neutral".
const ROWS = [
  {
    feature: 'Price per SMS',
    textlink: { value: '$0.001', type: 'good' },
    twilio:   { value: '$0.0079+', type: 'bad' },
    messagebird: { value: '$0.01+', type: 'bad' },
    eztexting:   { value: '$0.03+', type: 'bad' },
  },
  {
    feature: '10DLC / A2P registration',
    textlink: { value: 'Not required', type: 'good' },
    twilio:   { value: '2–6 weeks', type: 'bad' },
    messagebird: { value: 'Required', type: 'bad' },
    eztexting:   { value: 'Required', type: 'bad' },
  },
  {
    feature: 'Identity verification',
    textlink: { value: 'Not required', type: 'good' },
    twilio:   { value: 'Required', type: 'bad' },
    messagebird: { value: 'Required', type: 'bad' },
    eztexting:   { value: 'Required', type: 'bad' },
  },
  {
    feature: 'Two-way messaging',
    textlink: { value: true, type: 'good' },
    twilio:   { value: true, type: 'good' },
    messagebird: { value: true, type: 'good' },
    eztexting:   { value: false, type: 'bad' },
  },
  {
    feature: 'International delivery',
    textlink: { value: '180+ countries', type: 'good' },
    twilio:   { value: 'Yes (premium)', type: 'neutral' },
    messagebird: { value: 'Yes', type: 'neutral' },
    eztexting:   { value: 'US/CA only', type: 'bad' },
  },
  {
    feature: 'Content restrictions',
    textlink: { value: 'P2P — minimal', type: 'good' },
    twilio:   { value: 'A2P — heavy', type: 'bad' },
    messagebird: { value: 'A2P — heavy', type: 'bad' },
    eztexting:   { value: 'A2P — heavy', type: 'bad' },
  },
  {
    feature: 'Setup time',
    textlink: { value: 'Under 5 min', type: 'good' },
    twilio:   { value: 'Days–weeks', type: 'bad' },
    messagebird: { value: 'Days–weeks', type: 'bad' },
    eztexting:   { value: 'Hours', type: 'neutral' },
  },
  {
    feature: 'GoHighLevel native integration',
    textlink: { value: true, type: 'good' },
    twilio:   { value: true, type: 'good' },
    messagebird: { value: false, type: 'bad' },
    eztexting:   { value: false, type: 'bad' },
  },
];

const renderCell = (cell) => {
  if (typeof cell.value === 'boolean') {
    return cell.value
      ? <span className="lp-good"><LuCheck size={14} strokeWidth={2.5} /></span>
      : <span className="lp-bad"><LuX size={14} strokeWidth={2.5} /></span>;
  }
  if (cell.type === 'good') {
    return <span className="lp-good"><LuCheck size={14} strokeWidth={2.5} /> {cell.value}</span>;
  }
  if (cell.type === 'bad') {
    return <span className="lp-bad">{cell.value}</span>;
  }
  return <span style={{ color: 'var(--lp-text-muted)' }}>{cell.value}</span>;
};

export default function ProductComparisonTable() {
  const navigate = useNavigate();

  return (
    <>
      <Head
        title="TextLink vs Twilio vs Aggregators — SMS Comparison"
        description="Side-by-side comparison: TextLink vs Twilio, MessageBird, and EZTexting. Price per SMS, 10DLC requirements, content restrictions, setup time, international delivery, and CRM integrations."
        canonical="/sms-product-comparasings"
      />
      <HeaderOne />

      <main className="lp">
        <div className="lp-shell lp-shell--wide">

          <header className="lp-hero">
            <span className="lp-eyebrow">SMS provider comparison</span>
            <h1 className="lp-h1">
              TextLink vs the rest.<br />
              <span className="lp-h1-accent">The honest breakdown.</span>
            </h1>
            <p className="lp-lead">
              Real, line-by-line comparison of TextLink against Twilio, MessageBird, and EZTexting. Price, 10DLC requirements, content restrictions, setup time, and integrations — no marketing spin.
            </p>
            <div className="lp-cta-row">
              <button className="lp-btn lp-btn--primary" onClick={() => navigate('/signup')}>
                Try free <LuArrowRight size={15} strokeWidth={2} />
              </button>
              <Link className="lp-btn lp-btn--ghost" to="/pricing">See pricing</Link>
            </div>
          </header>

          <section className="lp-section">
            <h2 className="lp-h2">Feature-by-feature</h2>
            <p className="lp-sub">The differences that show up on your bill — and in your launch timeline.</p>

            <div className="lp-table-wrap">
              <table className="lp-table">
                <thead>
                  <tr>
                    <th></th>
                    <th className="lp-th-us">TextLink</th>
                    <th>Twilio</th>
                    <th>MessageBird</th>
                    <th>EZTexting</th>
                  </tr>
                </thead>
                <tbody>
                  {ROWS.map((row, i) => (
                    <tr key={i}>
                      <th scope="row" className="lp-feat-cell">{row.feature}</th>
                      <td>{renderCell(row.textlink)}</td>
                      <td>{renderCell(row.twilio)}</td>
                      <td>{renderCell(row.messagebird)}</td>
                      <td>{renderCell(row.eztexting)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="lp-section">
            <h2 className="lp-h2">Where each provider actually fits</h2>
            <p className="lp-sub">Honest take, even on competitors.</p>

            <div className="lp-grid-2">
              <div className="lp-card">
                <h3 className="lp-card-h">TextLink — best for</h3>
                <p className="lp-card-p">
                  Agencies, GoHighLevel users, SaaS founders, dispatchers, and anyone running consistent SMS volume who doesn't want to pay aggregator markup or spend a month on 10DLC paperwork.
                </p>
              </div>
              <div className="lp-card">
                <h3 className="lp-card-h">Twilio — best for</h3>
                <p className="lp-card-p">
                  Enterprise compliance teams who need toll-free verified short codes and have months of regulatory budget. Overkill for most growing businesses.
                </p>
              </div>
              <div className="lp-card">
                <h3 className="lp-card-h">MessageBird — best for</h3>
                <p className="lp-card-p">
                  EU-based companies prioritizing WhatsApp + SMS unified messaging. Pricing is similar to Twilio.
                </p>
              </div>
              <div className="lp-card">
                <h3 className="lp-card-h">EZTexting — best for</h3>
                <p className="lp-card-p">
                  US-only marketers running occasional broadcast campaigns who don't need APIs or two-way conversations.
                </p>
              </div>
            </div>
          </section>

          <section className="lp-cta">
            <h2 className="lp-h2">Try it before deciding.</h2>
            <p className="lp-sub">300 free messages on signup, no credit card. See the price difference on your own traffic.</p>
            <div className="lp-cta-row">
              <button className="lp-btn lp-btn--primary" onClick={() => navigate('/signup')}>
                Try free <LuArrowRight size={15} strokeWidth={2} />
              </button>
              <Link className="lp-btn lp-btn--ghost" to="/how-textlink-works">How it works</Link>
            </div>
          </section>

        </div>
      </main>

      <FooterOne />
    </>
  );
}
